// src/utils/analytics.js
import { analytics } from '../config/firebaseConfig';
import { logEvent } from 'firebase/analytics';
import { setUserId, setUserProperties } from 'firebase/analytics';

// Predefined event names for consistent tracking
export const EVENTS = {
  // User authentication events
  LOGIN: 'login',
  SIGNUP: 'sign_up',
  LOGOUT: 'logout',
  
  // Navigation events
  PAGE_VIEW: 'page_view',
  NAVIGATION: 'navigation',
  MENU_TOGGLE: 'menu_toggle',
  MOBILE_NAVIGATION: 'mobile_navigation',
  
  // Search events
  SEARCH_INITIATED: 'search_initiated',
  SEARCH_STOCK: 'search_stock',
  SEARCH_RESULTS: 'search_results',
  SEARCH_RESULT_CLICK: 'search_result_click',
  SEARCH_NO_RESULTS: 'search_no_results',
  
  // Filter events
  FILTER_CHANGE: 'filter_change',
  FILTER_RESET: 'filter_reset',
  PREDEFINED_FILTER: 'predefined_filter',
  FILTER_SECTION_TOGGLE: 'filter_section_toggle',
  
  // Portfolio events
  ADD_TO_PORTFOLIO: 'add_to_portfolio',
  REMOVE_FROM_PORTFOLIO: 'remove_from_portfolio',
  VIEW_PORTFOLIO: 'view_portfolio',
  UPDATE_PORTFOLIO_ITEM: 'update_portfolio_item',
  
  // Watchlist events
  ADD_TO_WATCHLIST: 'add_to_watchlist',
  REMOVE_FROM_WATCHLIST: 'remove_from_watchlist',
  VIEW_WATCHLIST: 'view_watchlist',
  
  // Settings events
  CURRENCY_CHANGE: 'currency_change',
  THEME_CHANGE: 'theme_change',
  
  // Chart interactions
  CHART_VIEW_CHANGE: 'chart_view_change',
  CHART_PERIOD_CHANGE: 'chart_period_change',
  
  // UI interactions
  SORT_RESULTS: 'sort_results',
  PAGINATION: 'pagination',
  MODAL_OPEN: 'modal_open',
  MODAL_CLOSE: 'modal_close',
  
  // Content engagement
  ARTICLE_VIEW: 'article_view',
  ARTICLE_TIME_SPENT: 'article_time_spent',

  // Article related events
  VIEW_ARTICLES: 'view_articles',
  VIEW_ARTICLE_DETAIL: 'view_article_detail',
  CLICK_ARTICLE: 'click_article',
  CLICK_RELATED_ARTICLE: 'click_related_article',
  SHARE_ARTICLE: 'share_article',
  FILTER_ARTICLES_BY_CATEGORY: 'filter_articles_by_category',
  
  // Error events
  ERROR_OCCURRED: 'error_occurred',
  API_ERROR: 'api_error',
};

// User identification
export const identifyUser = (user) => {
  if (analytics && user) {
    // Set the user ID from Firebase Auth
    setUserId(analytics, user.uid);
    
    // Set user properties for segmentation
    setUserProperties(analytics, {
      email_domain: user.email.split('@')[1],
      account_age_days: calculateAccountAgeDays(user.metadata.creationTime),
      last_login_days_ago: calculateDaysAgo(user.metadata.lastSignInTime),
      user_segment: determineUserSegment(user)
    });
  }
};

// Helper for tracking search events with enhanced parameters
export const trackSearchEvent = (searchParams = {}) => {
  const {
    searchTerm = "",
    sector = "All",
    activeFilters = {},
    currency = "USD",
    resultsCount = 0,
    queryTimeMs = 0,
    sortBy = "",
    sortOrder = ""
  } = searchParams;
  
  // Create standardized filter params for analytics
  const filterParams = {};
  
  // Process active filters
  Object.entries(activeFilters).forEach(([key, config]) => {
    if (typeof config === 'object' && config.value > 0) {
      filterParams[`filter_${key}`] = config.value;
    }
  });
  
  // Calculate metrics about the search
  const filterCount = Object.keys(filterParams).length;
  const hasFilters = filterCount > 0 ? "yes" : "no";
  const hasResults = resultsCount > 0 ? "yes" : "no";
  
  // Log the search event with comprehensive parameters
  trackEvent(EVENTS.SEARCH_STOCK, {
    search_term: searchTerm || "[empty]",
    sector,
    currency,
    filter_count: filterCount,
    has_filters: hasFilters,
    has_results: hasResults,
    results_count: resultsCount,
    query_time_ms: queryTimeMs,
    sort_by: sortBy,
    sort_order: sortOrder,
    ...filterParams // Add all individual filter parameters
  });
  
  // Track separate event for the results state
  if (resultsCount === 0 && searchTerm) {
    trackEvent(EVENTS.SEARCH_NO_RESULTS, {
      search_term: searchTerm,
      filter_count: filterCount
    });
  }
};

// Track filter usage
export const trackFilterChange = (filterData = {}) => {
  const {
    filterName = "",
    oldValue = 0,
    newValue = 0,
    isUserAction = true,
    source = "manual" // or "preset"
  } = filterData;
  
  trackEvent(EVENTS.FILTER_CHANGE, {
    filter_name: filterName,
    old_value: oldValue,
    new_value: newValue,
    delta: newValue - oldValue,
    is_increase: newValue > oldValue ? "yes" : "no",
    is_user_action: isUserAction ? "yes" : "no",
    source
  });
};

// Track when users apply predefined filter sets
export const trackPredefinedFilter = (filterData = {}) => {
  const {
    filterId = "",
    filterName = "",
    filterCount = 0,
    replacesExisting = false
  } = filterData;
  
  trackEvent(EVENTS.PREDEFINED_FILTER, {
    filter_id: filterId,
    filter_name: filterName,
    filter_count: filterCount,
    replaces_existing: replacesExisting ? "yes" : "no"
  });
};

// Enhanced portfolio tracking
export const trackPortfolioAction = (actionData = {}) => {
  const {
    actionType = "view", // "add", "remove", "update", "view"
    stockId = "",
    stockName = "",
    quantity = 0,
    price = 0,
    currency = "USD",
    purchaseDate = "",
    portfolioSize = 0
  } = actionData;
  
  const eventName = actionType === "add" ? EVENTS.ADD_TO_PORTFOLIO :
                    actionType === "remove" ? EVENTS.REMOVE_FROM_PORTFOLIO :
                    actionType === "update" ? EVENTS.UPDATE_PORTFOLIO_ITEM :
                    EVENTS.VIEW_PORTFOLIO;
  
  trackEvent(eventName, {
    stock_id: stockId,
    stock_name: stockName,
    quantity,
    price,
    currency,
    purchase_date: purchaseDate,
    portfolio_size: portfolioSize,
    transaction_value: price * quantity
  });
};

// Track user interactions with charts
export const trackChartInteraction = (chartData = {}) => {
  const {
    chartType = "", // "value", "growth"
    periodChange = "", // "day", "week", "month", "year"
    stockId = "", // if applicable
    portfolioView = false
  } = chartData;
  
  const eventName = periodChange ? 
    EVENTS.CHART_PERIOD_CHANGE : 
    EVENTS.CHART_VIEW_CHANGE;
  
  trackEvent(eventName, {
    chart_type: chartType,
    period: periodChange,
    stock_id: stockId || null,
    is_portfolio_view: portfolioView ? "yes" : "no"
  });
};

// Generic event tracking with enhanced error handling
export const trackEvent = (eventName, eventParams = {}) => {
  if (!analytics) {
    console.warn('Analytics is not initialized');
    return;
  }
  
  try {
    // Add user ID to event parameters if available
    const enhancedParams = {
      ...eventParams,
      timestamp: new Date().toISOString(),
      session_id: getSessionId()
    };
    
    logEvent(analytics, eventName, enhancedParams);
  } catch (error) {
    console.error('Error tracking event:', error);
  }
};

// Helper functions
const calculateAccountAgeDays = (creationTime) => {
  if (!creationTime) return 0;
  const creationDate = new Date(creationTime);
  const now = new Date();
  return Math.floor((now - creationDate) / (1000 * 60 * 60 * 24));
};

const calculateDaysAgo = (dateString) => {
  if (!dateString) return 0;
  const date = new Date(dateString);
  const now = new Date();
  return Math.floor((now - date) / (1000 * 60 * 60 * 24));
};

const determineUserSegment = (user) => {
  // Example logic for user segmentation - customize based on your needs
  if (!user.metadata) return 'new_user';
  
  const accountAgeDays = calculateAccountAgeDays(user.metadata.creationTime);
  const lastLoginDays = calculateDaysAgo(user.metadata.lastSignInTime);
  
  if (accountAgeDays < 7) return 'new_user';
  if (lastLoginDays > 30) return 'returning_inactive';
  if (accountAgeDays > 90) return 'power_user';
  return 'regular_user';
};

// Generate or retrieve a session ID
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('analytics_session_id');
  if (!sessionId) {
    sessionId = `${Date.now()}-${Math.random().toString(36).substring(2, 10)}`;
    sessionStorage.setItem('analytics_session_id', sessionId);
  }
  return sessionId;
};